/**
 * Auth Client
 *
 * Handles Google OAuth authentication with cross-domain session management.
 *
 * Flow:
 * 1. Login: navigateToGoogleLogin() → browser navigates to worker → 302 to Google
 * 2. Callback: worker creates session, stores token in localStorage via HTML bridge page
 * 3. Auth check: reads localStorage for session token, validates via /api/auth/me
 * 4. All API requests: include X-Session-Token header for cross-domain auth
 */

import { API_URL } from '@/env'
import { getAuthHeaders } from './auth'

const STORAGE_KEYS = {
  SESSION_TOKEN: 'auth_session_token',
  USER: 'auth_user',
} as const

export interface AuthUser {
  id: string
  email: string | null
  name: string | null
  picture: string | null
}

/**
 * Navigate to Google OAuth login endpoint
 * Worker sets cookies (first-party) and redirects to Google via 302.
 * Using direct navigation (not fetch) avoids third-party cookie blocking.
 */
export function navigateToGoogleLogin(): void {
  window.location.href = `${API_URL}/api/auth/google/login`
}

/**
 * Get the stored session token
 * On /auth/callback page, reads from URL hash first, then persists to localStorage
 */
export function getSessionToken(): string | null {
  // Check URL hash first (set by worker bridge page redirect)
  if (window.location.hash.startsWith('#token=')) {
    const token = decodeURIComponent(window.location.hash.slice(7))
    if (token) {
      localStorage.setItem(STORAGE_KEYS.SESSION_TOKEN, token)
      // Clean up URL hash
      window.history.replaceState(null, '', window.location.pathname)
      return token
    }
  }
  return localStorage.getItem(STORAGE_KEYS.SESSION_TOKEN)
}

/**
 * Get the stored user data (set by the worker bridge page)
 */
export function getStoredUser(): AuthUser | null {
  const raw = localStorage.getItem(STORAGE_KEYS.USER)
  if (!raw) return null
  try {
    return JSON.parse(raw) as AuthUser
  } catch {
    return null
  }
}

/**
 * Get current authenticated user
 * Checks localStorage first (set by bridge page), then validates via API
 */
export async function getCurrentUser(): Promise<AuthUser | null> {
  const token = getSessionToken()
  if (!token) return null

  try {
    const response = await fetch(`${API_URL}/api/auth/me`, {
      credentials: 'include',
      headers: { 'X-Session-Token': token },
    })

    if (response.status === 401) {
      // Session expired — clear stored data
      clearStoredAuth()
      return null
    }

    if (!response.ok) {
      return getStoredUser() // fallback to cached data
    }

    const data = await response.json()
    if (data.user) {
      // Update cached user data
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user))
      return data.user as AuthUser
    }

    return null
  } catch {
    // Network error — return cached user if available
    return getStoredUser()
  }
}

/**
 * Logout - invalidate session on server, clear local data
 */
export async function logoutUser(): Promise<void> {
  const token = getSessionToken()
  if (token) {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-Session-Token': token },
      })
    } catch {
      // Ignore network errors on logout
    }
  }
  clearStoredAuth()
}

/**
 * Clear stored auth data
 */
export function clearStoredAuth(): void {
  localStorage.removeItem(STORAGE_KEYS.SESSION_TOKEN)
  localStorage.removeItem(STORAGE_KEYS.USER)
}

/**
 * Merge device-based cards into the authenticated Google user account.
 *
 * After Google login, this migrates any cards created on this device
 * (under the device-based user) to the Google user, ensuring all cards
 * are visible regardless of which device was used to create them.
 *
 * Should be called once after successful Google login.
 */
export async function mergeDeviceToGoogleUser(): Promise<{
  merged: boolean
  cardsMigrated?: number
  reason?: string
}> {
  const token = getSessionToken()
  if (!token) {
    return { merged: false, reason: 'no_session' }
  }

  try {
    const response = await fetch(`${API_URL}/api/auth/merge-device`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'X-Session-Token': token,
        ...getAuthHeaders(), // includes X-Device-ID
      },
    })

    if (!response.ok) {
      return { merged: false, reason: 'server_error' }
    }

    const data = await response.json()
    return { merged: true, cardsMigrated: data.cardsMigrated ?? 0 }
  } catch {
    return { merged: false, reason: 'network_error' }
  }
}
